import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Stepper, Step } from 'react-form-stepper'
import Form from 'react-bootstrap/Form'
import { toast } from 'react-toastify'

import { selectTranslations } from '../../services/i18n/i18nSlice'
import { setLoading } from '../../services/global/globalSlice'

import { fetchCommunication, fetchCreateCommunication, fetchEditCommunication } from './CommunicationsApi'
import ExamPermissions from '../ExamScreen/_components/ExamPermissions'
import UserCommunicationsList from './_components/UserCommunicationsList'
import UploadButton from '../../components/UploadButton'
import SecureImage from '../../components/_partials/SecuredImage'
import { API_URL } from '../../config/constants'

export default function CommunicationDetails(props) {

    const t = useSelector(selectTranslations)
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { communication_id } = useParams()

    const [activeStep, setActiveStep] = useState(0)
    const [communicationData, setCommunicationData] = useState({})
    const [isUpdated, setIsUpdated] = useState(false)

    const handleDataChange = (e) => {
        const target = e.target
        const name = target.name
        const value = target.type === 'checkbox' ? target.checked : target.value
        setCommunicationData({
            ...communicationData,
            [name]: value
        })
        setIsUpdated(true)
    }

    const handleCtaChange = (e) => {
        const target = e.target
        const name = target.name
        const value = target.value
        setCommunicationData({
            ...communicationData,
            cta: {
                ...(communicationData?.cta || {}),
                [name]: value
            }
        })
        setIsUpdated(true)
    }

    const pad2 = (n) => n.toString().padStart(2, '0')
    const formatDateTimeLocal = (value) => {
        if (!value) return ''
        // If already in local datetime format (YYYY-MM-DDTHH:mm[...])
        if (typeof value === 'string' && value.length >= 16 && !value.includes('Z') && !value.includes('+')) {
            return value.substring(0,16)
        }
        const d = new Date(value)
        if (isNaN(d.getTime())) return ''
        const year = d.getFullYear()
        const month = pad2(d.getMonth() + 1)
        const day = pad2(d.getDate())
        const hours = pad2(d.getHours())
        const minutes = pad2(d.getMinutes())
        return `${year}-${month}-${day}T${hours}:${minutes}`
    }

    const handleCommunicationDataChange = (data) => {
        setCommunicationData(data)
        setIsUpdated(true)
    }

    const handleUploadIcon = (url) => {
        setCommunicationData({
            ...communicationData,
            icon_image_url: url
        })
        setIsUpdated(true)
    }

    const handleRemoveIcon = () => {
        setCommunicationData({
            ...communicationData,
            icon_image_url: ''
        })
        setIsUpdated(true)
    }

    const prepareCommunicationData = () => {
        const data = structuredClone(communicationData)
        if (data['start_date'])
            data['start_date'] = `${new Date(data['start_date']).toISOString().substring(0,19)}+00:00`
        if (data['end_date'])
            data['end_date'] = `${new Date(data['end_date']).toISOString().substring(0,19)}+00:00`
        else if (data['end_date'] === '')
            delete data.end_date

        if (data['allowed_groups']?.length > 0)
            data['allowed_groups'] = data['allowed_groups']?.map(g => g.group_id || g)
        if (data['allowed_users']?.length > 0)
            data['allowed_users'] = data['allowed_users']?.map(u => u.user_id || u)

        if (data['priority'] !== undefined && data['priority'] !== null && data['priority'] !== '')
            data['priority'] = parseInt(data['priority'])
        else
            delete data.priority

        if (data['cta'] && (!data['cta']?.title || !data['cta']?.url))
            delete data.cta

        return data
    }

    const handleCreate = async () => {
        dispatch(setLoading(true))
        const payload = prepareCommunicationData()
        const response = await fetchCreateCommunication(payload)
        if (response.success) {
            setCommunicationData({
                ...communicationData,
                communication_id: response.data.communication_id
            })
            toast.success(t.exam?.settings_saved || 'Impostazioni salvate')
            dispatch(setLoading(false))
            return 'ok'
        } else {
            toast.error(t.exam?.error_saving_exam || 'Errore nel salvataggio')
            dispatch(setLoading(false))
        }
    }

    const handleEdit = async () => {
        dispatch(setLoading(true))
        const payload = prepareCommunicationData()
        const response = await fetchEditCommunication(communicationData?.communication_id, payload)
        if (response.success) {
            toast.success(t.exam?.settings_saved || 'Impostazioni salvate')
            dispatch(setLoading(false))
            return 'ok'
        } else {
            toast.error(t.exam?.error_saving_exam || 'Errore nel salvataggio')
            dispatch(setLoading(false))
        }
    }

    const handleSave = async () => {
        if (communicationData?.communication_id) {
            return handleEdit()
        } else {
            return handleCreate()
        }
    }

    const handleNextStep = () => {
        switch (activeStep) {
            case 0:
                if (communicationData.title && communicationData.text) {
                    if (!communicationData?.communication_id) {
                        handleCreate().then(() => setActiveStep(1))
                    } else {
                        handleEdit().then(() => setActiveStep(1))
                    }
                } else {
                    toast.error(t.exam?.missing_title_description || 'Titolo e descrizione mancanti')
                }
            break;
            case 1:
                handleSave().then(() => {
                    navigate('/communications', { replace: true })
                })
            break;
            default:
                break;
        }
    }

    const renderNextLabel = () => {
        switch (activeStep) {
            case 0:
                return t.exam?.users || 'Utenti'
            case 1:
                return t.buttons?.edit_save || 'Salva'
            default:
                return t.exam?.proceed || 'Procedi'
        }
    }

    const loadCommunication = async () => {
        const _id = communicationData.communication_id || communication_id
        const response = await fetchCommunication(_id)
        if (response.success) {
            setCommunicationData(response.data)
            dispatch(setLoading(false))
        } else {
            console.error('error')
        }
    }

    useEffect(() => {
        if (!props?.isNew) {
            dispatch(setLoading(true))
            loadCommunication()
        } else {
            setCommunicationData({
                type: 'popup',
                title: '',
                text: '',
                icon_image_url: '',
                link: '',
                folder_id: '',
                cta: { title: '', url: '' },
                start_date: '',
                end_date: '',
                priority: 0,
                allowed_all: true,
                allowed_users: [],
                allowed_groups: [],
                disabled: false
            })
        }
    }, [])

    return (
        <section>
            <Stepper 
                activeStep={activeStep}
                styleConfig={{
                    activeBgColor: '#436094',
                    completedBgColor: '#436094',
                    inactiveBgColor: '#E2E2E2'
                }}
                nonLinear={!props?.isNew}
            >
                <Step label={t.exam?.info || 'Info'} onClick={() => setActiveStep(0)} />
                <Step label={t.exam?.users || 'Utenti'} onClick={() => setActiveStep(1)} />
                {!props?.isNew && <Step label={t.communications.views} onClick={() => setActiveStep(2)} />}
            </Stepper>
            <div className='container'>
                {
                    communicationData ?
                    <>
                        {activeStep === 0 ? (
                            <>
                                <div className='info_card'>
                                    <h2>{t.exam?.primary_info || 'Informazioni principali'}</h2>
                                    <div className='row'>
                                        <div className='col-6'>
                                            <Form.Group controlId='type'>
                                                <Form.Label>Tipo</Form.Label>
                                                <Form.Select name='type' value={communicationData.type || 'popup'} onChange={handleDataChange}>
                                                    <option value='popup'>Popup</option>
                                                    <option value='banner'>Banner</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </div>
                                        <div className='col-6'>
                                            <Form.Group controlId='priority'>
                                                <Form.Label>Priorità</Form.Label>
                                                <Form.Control type='number' name='priority' value={communicationData.priority ?? 0} onChange={handleDataChange} />
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <Form.Group controlId='title' className='mt-2'>
                                        <Form.Label>{t.exam?.title || 'Titolo'}*</Form.Label>
                                        <Form.Control type='text' value={communicationData.title || ''} name='title' placeholder={t.exam?.title || 'Titolo'} required onChange={handleDataChange} />
                                    </Form.Group>
                                    <Form.Group controlId='text' className='mt-2'>
                                        <Form.Label>Testo*</Form.Label>
                                        <Form.Control as='textarea' value={communicationData.text || ''} name='text' placeholder={'Testo'} onChange={handleDataChange} />
                                    </Form.Group>
                                    <div className='d-flex justify-content-between mt-2'>
                                        <Form.Group controlId='start_date' className='col-6'>
                                            <Form.Label>{t.exam?.start_date || 'Data inizio'}</Form.Label>
                                            <Form.Control className='minw-0' type='datetime-local' name='start_date' value={formatDateTimeLocal(communicationData.start_date)} onChange={handleDataChange} />
                                        </Form.Group>
                                        <Form.Group controlId='end_date' className='col-6 ps-2'>
                                            <Form.Label>{t.exam?.end_date || 'Data fine'}</Form.Label>
                                            <Form.Control className='minw-0' type='datetime-local' name='end_date' value={formatDateTimeLocal(communicationData.end_date)} onChange={handleDataChange} />
                                        </Form.Group>
                                    </div>
                                </div>
                                <div className='info_card mt-3'>
                                    <h2>{t.exam?.secondary_info || 'Informazioni secondarie'}</h2>
                                    <div className='row align-items-center'>
                                        <div className='col-6'>
                                            {
                                                communicationData?.icon_image_url ? (
                                                    <div className='d-flex align-items-end' style={{maxWidth:'350px'}}>
                                                        <span>Icona:</span>
                                                        <SecureImage className='w-100' imageUrl={`${API_URL}${communicationData.icon_image_url}`} style={{maxWidth:'45px'}} />
                                                        <div className=''>
                                                            <button className='secondary' type='button' onClick={handleRemoveIcon}>Rimuovi icona</button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <UploadButton label={'Aggiungi icona'} type='personalization' onUpload={handleUploadIcon} accept='image/png, image/gif, image/jpeg' showProgress={true} />
                                                )
                                            }
                                        </div>
                                        <div className='col-6'>
                                            <Form.Group controlId='link'>
                                                <Form.Label>Link</Form.Label>
                                                <Form.Control type='text' name='link' value={communicationData.link || ''} onChange={handleDataChange} />
                                            </Form.Group>
                                        </div>
                                    </div>
                                    <div className='row mt-2'>
                                        <div className='col-6'>
                                            <Form.Group controlId='folder_id'>
                                                <Form.Label>Folder ID</Form.Label>
                                                <Form.Control type='text' name='folder_id' value={communicationData.folder_id || ''} onChange={handleDataChange} />
                                            </Form.Group>
                                        </div>
                                        <div className='col-6'>
                                            <div className='d-flex mt-4'>
                                                <Form.Check
                                                    type='switch'
                                                    id='disabled'
                                                    name='disabled'
                                                    label={'Disabilitata'}
                                                    onChange={handleDataChange}
                                                    checked={communicationData?.disabled || false}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <hr />
                                    <div className='row'>
                                        <div className='col-6'>
                                            <Form.Group controlId='cta_title'>
                                                <Form.Label>CTA Title</Form.Label>
                                                <Form.Control type='text' name='title' value={communicationData?.cta?.title || ''} onChange={handleCtaChange} />
                                            </Form.Group>
                                        </div>
                                        <div className='col-6'>
                                            <Form.Group controlId='cta_url'>
                                                <Form.Label>CTA URL</Form.Label>
                                                <Form.Control type='text' name='url' value={communicationData?.cta?.url || ''} onChange={handleCtaChange} />
                                            </Form.Group>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : null}
                        {activeStep === 1 ? (
                            <>
                                <ExamPermissions examData={communicationData} onExamDataChange={handleCommunicationDataChange} />
                            </>
                        ) : null}
                        {activeStep === 2 ? (
                            <UserCommunicationsList communicationId={communicationData?.communication_id} />
                        ) : null}
                    </> : null
                }
            </div>
            <footer className='control-footer'>
                <div className='d-flex align-items-center justify-content-between'>
                    <div> 
                        <Link to={'/communications'}>
                            <u>{t.buttons?.exit_without_save || 'Esci senza salvare'}</u>
                        </Link>
                    </div>
                    {activeStep < 2 && <div className='d-flex justify-content-end'>
                        <button disabled={!isUpdated} className='secondary' onClick={handleSave}>
                            {t.buttons?.edit_save || 'Salva'}
                        </button>
                        <button disabled={!isUpdated} className='ms-2' onClick={handleNextStep}>
                            {renderNextLabel()}
                        </button>
                    </div>}
                </div>
            </footer>
        </section>
    )
}


